/**
 * @file LocationDropdown.js
 * @description A dropdown component for selecting a location.
 */

import React, { useState, useEffect } from 'react';
import { fetchLocations } from '../../../services/orgLevelService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const LocationDropdown = ({
    className,
    inlineLabel = true,
    tenantId,
    selectedLocation,
    onLocationChange,
    required = false,
    isPublic = false,
    companyCode = null,
    errorMessage = null,
}) => {
    const [locations, setLocations] = useState([]);
    const [defaultLabel, setDefaultLabel] = useState('Select Location');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getLocations = async () => {
            try {
                const data = await fetchLocations(tenantId, {
                    isPublic,
                    companyCode,
                });
                setLocations(data);

                const matchingLocation =
                    selectedLocation &&
                    data.find(loc => loc.id === selectedLocation);
                setDefaultLabel(
                    matchingLocation
                        ? matchingLocation.name
                        : 'Select Location',
                );
            } catch (error) {
                console.error('Could not fetch locations:', error);
                Toast.error(getText('msg_664d39956d42'));
            } finally {
                setIsLoading(false);
            }
        };

        getLocations();
    }, [tenantId, isPublic, companyCode]);

    useEffect(() => {
        if (selectedLocation && locations.length > 0) {
            const matchingLocation = locations.find(
                loc => loc.id === selectedLocation,
            );
            setDefaultLabel(
                matchingLocation ? matchingLocation.name : 'Select Location',
            );
        } else {
            setDefaultLabel('Select Location');
        }
    }, [selectedLocation, locations]);

    const locationOptions = [
        { value: null, label: getText('msg_9fa73f5b8043') },
        ...locations.map(format => ({
            value: format.id,
            label: format.name,
        })),
    ];

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="location">
                {getText('msg_d219c68101f5')}{required && <span className="text-danger">*</span>}
                {/* <span className="text-danger">*</span> */}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    defaultValue={defaultLabel}
                    options={locationOptions}
                    onChange={value => onLocationChange(value)}
                    value={selectedLocation}
                    disabled={locations.length === 0 || isLoading}
                />
            </LoadingWrapper>
            {errorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </div>
    );
};

export default LocationDropdown;
